import { App } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchOrganizationById,
  updateOrganization
} from 'services/api/organizations';
import { OrganizationDetail } from 'types/api/organizations';

import { showErrorMessage } from 'utils/showErrorMessage';

const useOrganizationSettings = (id: string) => {
  const { message } = App.useApp();

  const [organization, setOrganization] = useState<OrganizationDetail | null>(
    null
  );

  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchOrganizationById(id);
        if (!cancelled) setOrganization(res);
      } catch {
        // swallow — empty settings on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, refreshKey]);

  const settings = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => (organization?.settings ?? {}) as Record<string, any>,
    [organization?.settings]
  );

  const onUpdateSettings = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        setLoading(true);
        // BE accepts settings as a jsonb object directly — no more
        // JSON.stringify wrapper.
        await updateOrganization(id, {
          settings: { ...settings, ...values }
        });
        message.success('Successfully saved');
        setRefreshKey(k => k + 1);
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setLoading(false);
      }
    },
    [id, settings, message]
  );

  return {
    loading,
    settings,
    onUpdateSettings
  };
};

export default useOrganizationSettings;
